import { useMemo } from 'react';

import { useFilterContext } from '../../../filters/contexts/FilterProvider';
import type { MultiMetricDataMap } from '../../DataProvider/MultiMetricProvider';
import type { DataContext } from '../TextChart/utils/handlebars-setup';
import { processContentSync } from '../TextChart/utils/use-process-content';

export type AxisConfig = { min: number; max: number } | undefined;

type UseComputedAxisOptions = {
  yAxisMin?: string;
  yAxisMax?: string;
  multiData?: MultiMetricDataMap;
};

export const useComputedAxis = ({
  yAxisMin,
  yAxisMax,
  multiData,
}: UseComputedAxisOptions): AxisConfig => {
  const { activeFilterMap } = useFilterContext();

  const computedAxis = useMemo(() => {
    const yAxisMinTemplate = yAxisMin?.trim() ?? '';
    const yAxisMaxTemplate = yAxisMax?.trim() ?? '';
    const hasTemplates =
      yAxisMinTemplate.length > 0 || yAxisMaxTemplate.length > 0;

    if (!hasTemplates || !multiData) {
      return undefined;
    }

    const filters = Array.from(activeFilterMap.values());
    const dataContext: DataContext = { filters, multiData };

    try {
      const minStr =
        yAxisMinTemplate.length > 0
          ? processContentSync(yAxisMinTemplate, dataContext)
          : '';
      const maxStr =
        yAxisMaxTemplate.length > 0
          ? processContentSync(yAxisMaxTemplate, dataContext)
          : '';

      const parsedMin =
        minStr.trim().length > 0 ? Number(minStr.trim()) : undefined;
      const parsedMax =
        maxStr.trim().length > 0 ? Number(maxStr.trim()) : undefined;

      if (
        parsedMin === undefined ||
        parsedMax === undefined ||
        !Number.isFinite(parsedMin) ||
        !Number.isFinite(parsedMax)
      ) {
        return undefined;
      }

      return { min: parsedMin, max: parsedMax };
    } catch (error) {
      console.error('Error computing axis bounds:', error);
      return undefined;
    }
  }, [yAxisMin, yAxisMax, multiData, activeFilterMap]);

  return computedAxis;
};
